import { useState, ReactElement, useEffect } from 'react';
import { NextPageContext } from 'next';
import dynamic from 'next/dynamic';
import { isEmpty, unset, compact } from 'lodash';
import { stringify } from 'querystring';
import { useRouter } from 'next/router';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { serialize, userIsAdmin, userCanManageAccount } from 'lib/helpers';
import {
  CreatorLink, Location, MilestoneCounts, Post,
} from 'lib/types';
import { ProfileUser } from 'lib/types/users';
import { redirectTo404, USER_TYPE_IDS } from 'lib/constants';
import { getUserIdFromContext } from 'server/api/services/auth';
import { imageUrl } from 'lib/utils/imageUtils';
import { Profile } from 'features/Profile';
import { postFilterDescription, pluralizeRole } from 'lib/filters';
import { isEmptyProfileQuery } from 'lib/utils/filterUtils';
import { UserService } from 'server/api/services/users/user';
import { CreatorLinksFooter } from 'components/CreatorLinksFooter';
import { CreatorLinkService } from 'server/api/services/creatorLinkService';
import { queryToCriteria } from 'features/Creators/utils';
import { PostServiceStats, PostServiceMeta } from 'server/api/types';
import { MilestoneService } from 'server/api/services/milestones';
import { PostSearch } from 'server/es/indexes/posts/search';
import { modelToSlug } from 'lib/utils/modelSlugUtils';
import { UserRolesProvider } from 'lib/contexts/userRolesContext';
import { UserListsProvider } from 'lib/contexts/userListsContext';
import { TeamsProvider } from 'lib/contexts/teamsContext';
import { generateProfilePageStructuredData } from 'lib/utils/structuredDataUtils';
import { ProfilePageStructuredData } from 'lib/types/structuredData';
import { withTimeout } from 'lib/utils/timeoutUtils';

const TIMEOUT_MS = 400;
const Celebrate = dynamic(
  () => import('components/Celebrate').then((mod) => mod.Celebrate),
  { ssr: false },
);

export const getServerSideProps = async (context: NextPageContext) => {
  const { query } = context;
  const usernameInUrl = query.profileuser as string;

  // fast currentUserId lookup
  const currentUserId = await getUserIdFromContext(context);

  // Run profile fetch and current user fetch in parallel
  const [profileUser, currentUser] = await Promise.all([
    UserService.getProfileByUsername(usernameInUrl, currentUserId, true),
    currentUserId ? UserService.getByIdForContext(currentUserId) : Promise.resolve(undefined),
  ]);

  if (!profileUser) {
    return redirectTo404;
  }
  const profileUserId = profileUser.id;

  const profileIsHidden = profileUser.isHidden
    && currentUserId !== profileUserId
    && !userIsAdmin(currentUser as any)
    && !userCanManageAccount(currentUser as any, profileUser as any);

  if (profileIsHidden) {
    return ({
      redirect: {
        destination: `/explore?users=${modelToSlug(profileUser, 'username')}`,
        permanent: false,
      },
    });
  }

  unset(query, 'profileuser');

  const baseUrl = `https://gondola.cc/${profileUser.username}`;
  const url = !isEmpty(query) ? `${baseUrl}?${stringify(query)}` : baseUrl;

  let title = '';
  if (isEmptyProfileQuery(query)) {
    title = `${profileUser.name}'s posts on Gondola`;
  } else {
    const baseDesc = `${profileUser.name}'s posts`;
    title = postFilterDescription(query, baseDesc) as string;
  }

  const criteria = queryToCriteria(query);

  const creatorLinks = await CreatorLinkService
    .asUserId(currentUser?.id)
    .getFooterLinks(criteria.byRoleId || compact([profileUser.defaultRoleId]) || [],
      profileUser.location?.name);

  const location = profileUser.location || null;
  const role = () => {
    if (typeof query.role === 'string') {
      return pluralizeRole(query.role.split('~')[0]);
    }
    if (query?.role && query.role.length > 0) {
      return pluralizeRole(query?.role[0].split('~')[0]);
    }
    if (profileUser.defaultRole) {
      return pluralizeRole(profileUser.defaultRole.name || '');
    }
    return 'Creators';
  };

  const { cardUrl } = profileUser;

  // Only need to get profile details if currentUser is anonymous
  const [
    profileStats,
    milestoneCounts,
    totalFollowers,
    postResponse,
  ] = currentUser ? [null, null, null, null] : await Promise.all([
    PostSearch.getProfileStats(profileUserId, !currentUserId).catch(() => null),
    MilestoneService.getAchievedMilestoneCount(profileUserId).catch(() => null),
    UserService.getTotalFollowersByUserId(profileUserId).catch(() => null),
    withTimeout(PostSearch.profileSearch({
      ...criteria,
      profileUserId,
    }, currentUser), TIMEOUT_MS).catch(() => null),
  ]);

  return {
    props: {
      profileUser: serialize(profileUser),
      url,
      title,
      moreRoles: serialize(creatorLinks?.moreRoles),
      moreLocations: serialize(creatorLinks?.moreLocations),
      location: serialize(location),
      role: role(),
      cardUrl: serialize(cardUrl),
      stats: serialize(profileStats?.meta.stats),
      milestoneCounts: serialize(milestoneCounts),
      totalFollowers,
      isOwnProfile: currentUser?.id === profileUserId,
      posts: serialize(postResponse ? postResponse.data : null),
      meta: serialize(postResponse ? postResponse.meta : null),
    },
  };
};

interface Props {
  profileUser: ProfileUser;
  url: string;
  title: string;
  moreRoles: CreatorLink[];
  moreLocations: CreatorLink[];
  location?: Location;
  role: string;
  cardUrl?: string;
  stats?: PostServiceStats;
  milestoneCounts?: MilestoneCounts;
  totalFollowers?: number;
  isOwnProfile?: boolean;
  posts?: Post[] | null;
  meta?: PostServiceMeta | null;
}

const ProfilePage = ({
  profileUser,
  url,
  title,
  moreRoles,
  moreLocations,
  location,
  role,
  cardUrl,
  stats,
  milestoneCounts,
  totalFollowers,
  isOwnProfile = false,
  posts,
  meta,
}: Props) => {
  const router = useRouter();

  const [richTitle, setRichTitle] = useState<string | ReactElement>('');
  const [_title, setTitle] = useState(title);
  const userSummary = profileUser.userSummary ? profileUser.userSummary
    : `${profileUser.name} is a ${USER_TYPE_IDS[profileUser.userTypeId].toLowerCase()} on Gondola. "${profileUser.blurb}"`;

  let structuredData: ProfilePageStructuredData | undefined;
  try {
    structuredData = generateProfilePageStructuredData(profileUser, stats, totalFollowers);
  } catch (err) {
    console.error('Error generating structured data', err);
  }

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (isEmptyProfileQuery(router.query)) {
      setRichTitle('');
      setTitle(`${profileUser.name}'s posts on Gondola`);
    } else {
      const baseDesc = `${profileUser.name}'s posts`;
      setRichTitle(postFilterDescription(router.query, baseDesc, true));
      setTitle(postFilterDescription(router.query, baseDesc) as string);
    }
  }, [router]);

  return (
    <>
      <GondolaHead
        title={isEmptyProfileQuery(router.query) ? profileUser.name : title}
        image={profileUser.avatarUrl}
        description={userSummary}
        url={url}
        cardUrl={imageUrl(cardUrl)}
        robots={profileUser.noIndex ? 'noindex' : ''}
        structuredData={structuredData}
      />
      <UserRolesProvider user={profileUser}>
        <UserListsProvider includeUsers>
          <TeamsProvider>
            <Profile
              title={_title}
              richTitle={richTitle}
              profileUser={profileUser}
              milestoneCounts={milestoneCounts}
              totalFollowers={totalFollowers}
              stats={stats}
              serverSidePosts={posts}
              serverSidePostMeta={meta}
            />
          </TeamsProvider>
        </UserListsProvider>
      </UserRolesProvider>
      <CreatorLinksFooter
        location={location}
        role={role || ''}
        roleName={role || ''}
        moreRoles={moreRoles || []}
        moreLocations={moreLocations || []}
      />
      {!!isOwnProfile && <Celebrate />}
    </>
  );
};

export default ProfilePage;
